/*
 * Project: Goalscape
 * File:    textUtils.js
 * Path:    src/utils/textUtils.js
 * Description: Utility functions for text manipulation and formatting.
 * Author:  Akmal Djumakhodjaev
 * Created: [Creation Date - YYYY-MM-DD]
 * Modified: 2025-03-15
 * AI Hint: Implement text summarization to provide concise previews of long text content.
 */

/*
 * Function: makeLinksClickable
 * Description: Converts URLs in text into clickable links with styling.
 * Parameters:
 *   text: string - The text to process.
 * Returns: Array<string|JSX.Element> - Array of text segments and link elements.
 * Usage:
 *   const content = makeLinksClickable('Visit https://example.com for more info');
 * AI Hint: Implement URL shortening to reduce the length of displayed links.
 */
export const makeLinksClickable = (text) => {
    if (!text) return '';

    const urlRegex = /(https?:\/\/[^\s<]+[^<.,:;"')\]\s])/g;

    return text.split(urlRegex).map((part, i) => {
        if (part.match(urlRegex)) {
            return (
                <a
                    key={i}
                    href={part}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        color: '#fff',
                        textDecoration: 'none',
                        fontWeight: 'bold',
                        cursor: 'crosshair',
                        '&:hover': {
                            textDecoration: 'underline'
                        }
                    }}
                >
                    {part}
                </a>
            );
        }
        return part;
    });
};

/*
 * Function: copyToClipboard
 * Description: Copies the given text to the clipboard.
 * Parameters:
 *   text: string - The text to copy.
 * Returns: Promise<void>
 * Usage:
 *   copyToClipboard('Text to copy')
 *     .then(() => console.log('Copied!'))
 *     .catch(err => console.error('Failed to copy:', err));
 */
export const copyToClipboard = async (text) => {
  if (!navigator.clipboard) {
    // Clipboard API not available
    alert('Clipboard access is not available in your browser.');
    return Promise.reject('Clipboard API not available');
  }
  try {
    await navigator.clipboard.writeText(text);
    // alert('Copied to clipboard!'); // Removed this line
  } catch (err) {
    console.error('Failed to copy text: ', err);
    alert('Failed to copy text. See console for details.');
    return Promise.reject(err);
  }
};

/**
 * Determines whether to use black or white text based on the background color's luminance.
 * @param {string} hexcolor - The background color in hex format (e.g., "#RRGGBB").
 * @returns {string} - Returns "#000000" (black) or "#FFFFFF" (white) for the text color.
 */
export const getContrastYIQ = (hexcolor) => {
  if (!hexcolor) return '#000000'; // Default to black if no color provided

  hexcolor = hexcolor.replace("#", "");
  if (hexcolor.length === 3) {
    hexcolor = hexcolor.split('').map(char => char + char).join('');
  }
  if (hexcolor.length !== 6) {
    return '#000000'; // Default to black for invalid hex codes
  }

  const r = parseInt(hexcolor.substr(0, 2), 16);
  const g = parseInt(hexcolor.substr(2, 2), 16);
  const b = parseInt(hexcolor.substr(4, 2), 16);

  if (isNaN(r) || isNaN(g) || isNaN(b)) {
    return '#000000'; // Default to black if parsing fails
  }

  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 128 ? '#000000' : '#FFFFFF';
};
